import React, { useCallback, useState } from 'react';
import axios from 'axios';

import { useClaim } from '~/components/ClaimContainer';
import { claimBarCellMaxWidth } from '~/components/ClaimPage/ClaimPageBar/ClaimPageBarUtils';
import { ContactEntity } from '~/components/Contact';
import { PERMISSION_ACTIONS, PERMISSION_VERBS, RestrictedPermissions } from '~/components/core';
import { useCms } from '~/components/hooks/useCms';
import { PreDefinedFieldIsolated } from '~/components/IncidentConfiguration/ConfiguredFields';
import { extractPreDefinedFieldsFromClaim } from '~/components/IncidentConfiguration/utils';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isConfigurableFnol, isFeatureEnabled, reportAxiosError } from '~/Utils';

import GenericClaimBarField from './GenericClaimBarField';

type ReporterProps = {
  displayName?: string;
};

const getReporterUpdateUrl = (claimId: number) => `/api/v1/claims/${claimId}/reporter_contact`;

const updateReporter = async (claimId: number, reporterIdFieldName: string, reporterId: number): Promise<void> => {
  return await axios.put(getReporterUpdateUrl(claimId), { [reporterIdFieldName]: reporterId });
};

const EditableReporter = () => {
  const { claim, onAsyncClaimUpdate } = useClaim({ claimRequired: true });

  const [isFetching, setIsFetching] = useState(false);
  const preDefinedFields = extractPreDefinedFieldsFromClaim(claim);

  const handleUpdateReporter = useCallback(
    async (fieldName: string, fieldValue: number) => {
      setIsFetching(true);
      try {
        await updateReporter(claim.id, fieldName, fieldValue);
        await onAsyncClaimUpdate();
      } catch (error) {
        reportAxiosError(error);
      }
      setIsFetching(false);
    },
    [claim.id, onAsyncClaimUpdate]
  );

  return (
    <RestrictedPermissions action={PERMISSION_ACTIONS.REPORTER} verb={PERMISSION_VERBS.WRITE}>
      <PreDefinedFieldIsolated
        id="reporter_contact_id"
        fields={preDefinedFields}
        value={claim.reporter_contact_id}
        contactDisplayName={claim.reporter_contact_full_name}
        hideReadOnlyTitle
        onUpdate={handleUpdateReporter}
        disabled={isFetching}
        maxWidth={claimBarCellMaxWidth}
      />
    </RestrictedPermissions>
  );
};

const ReadOnlyReporter = () => {
  const { claim } = useClaim({ claimRequired: true });
  return (
    <ContactEntity
      contactId={claim.reporter_contact_id}
      contactDisplayName={claim.reporter_contact_full_name}
      maxWidth={claimBarCellMaxWidth}
    />
  );
};

const Reporter = ({ displayName = 'Reporter' }: ReporterProps) => {
  const { claim } = useClaim({ claimRequired: true });
  const { userOrganization } = useCms();

  // The reporter_contact route refuses every organization without permissions enforcement.
  const isEditable =
    isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.PERMISSIONS_ENFORCEMENT) &&
    isConfigurableFnol({ userOrganization, claimType: claim.type, claim });

  const content = isEditable ? <EditableReporter /> : <ReadOnlyReporter />;

  return <GenericClaimBarField title={displayName} allowWrapping={false} content={content} />;
};

export default Reporter;
